import React, { useState, useMemo } from 'react';
import { AccordionStackable } from 'ui/AccordionStackable/AccordionStackable';
import { MediaGallery } from 'ui/MediaGallery';
import { DocumentLinks } from 'ui/DocumentLinks';
import { SvgIcon } from 'ui/SvgIcon';
import ExpandArrowsIcon from 'ui/Icons/expan-arrows.component.svg';
import PersonIcon from 'ui/Icons/person.component.svg';
import { SeeMoreTextCollapsible } from 'ui/SeeMoreTextCollapsible';
import { ColumnBulletPoints } from 'ui/ColumnBulletPoints/ColumnBulletPoints';
import { HtmlStringRenderer } from 'components/HtmlStringRenderer';
import { useCurrentWidth } from 'effects';
import { transformGalleryItems, transformDocumentItems, createVimeoThumbnailUpload } from '../utils/dataTransformers';
import { IExploreHotelAccommodation } from 'services/BackendApi/types/HotelCollectionDetailResponse';

interface IRoomsAccordionsProps {
  accommodations: IExploreHotelAccommodation[];
}

/**
 * RoomsAccordions Component
 * Renders a list of room accommodations with expandable details
 */
export const RoomsAccordions: React.FC<IRoomsAccordionsProps> = ({ accommodations }) => {
  // State to track which accordions are open (by room name)
  const [openAccordions, setOpenAccordions] = useState<Set<string>>(new Set());

  // Sort rooms alphabetically by name
  const sortedAccommodations = useMemo(() => {
    return [...accommodations].sort((a, b) => a.name.localeCompare(b.name));
  }, [accommodations]);

  const { isMdOrLarger } = useCurrentWidth();

  const toggleAccordion = (roomName: string) => {
    setOpenAccordions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(roomName)) {
        newSet.delete(roomName);
      } else {
        newSet.add(roomName);
      }
      return newSet;
    });
  };

  return (
    <div className="flex flex-col">
      {sortedAccommodations.map((accommodation, index) => {
        const isOpen = openAccordions.has(accommodation.name);
        const hasGallery = accommodation.gallery && accommodation.gallery.length > 0;
        const hasDocuments = accommodation.documents && accommodation.documents.length > 0;
        const hasAmenities = accommodation.amenities && accommodation.amenities.length > 0;
        const hasBenefits = accommodation.benefits && accommodation.benefits.trim().length > 0;

        return (
          <AccordionStackable
            key={`${accommodation.name}-${index}`}
            isOpen={isOpen}
            onToggle={() => toggleAccordion(accommodation.name)}
            wrapperClassName="border-gray-20"
            buttonClassName="px-20px py-[10px] gap-[20px]"
            contentWrapperClassName="px-20px py-[20px]"
            summary={
              <div className="flex flex-row items-center gap-[20px] flex-1">
                {/* Room name and type */}
                <div className="flex flex-row items-center gap-[5px] flex-1">
                  <span className={`font-pt-sans text-[15px] leading-[19px] text-black ${isOpen ? 'font-bold' : ''}`}>
                    {accommodation.name}
                  </span>
                  {accommodation.roomType && (
                    <span className={`font-pt-sans text-[15px] leading-[19px] text-black ${isOpen ? 'font-bold' : ''}`}>
                      ({accommodation.roomType})
                    </span>
                  )}
                </div>

                {/* LIVE RATES badge */}
                {accommodation.activeForLiveRates && (
                  <div className="hidden md:flex justify-center items-center gap-[10px] px-[5px] py-[2px] bg-teal-40 border border-teal-80 rounded-[2px]">
                    <span className="font-hurmegeometric-sans text-[12px] leading-[15px] text-black tracking-[0.04em]">
                      LIVE RATES
                    </span>
                  </div>
                )}

                {/* Room size */}
                <div className="hidden md:flex flex-row items-center gap-[5px] min-w-[120px]">
                  <SvgIcon IconComponent={ExpandArrowsIcon} className="w-[18px] h-[18px]" />
                  <span className="font-pt-sans text-[15px] leading-[19px] text-gray-100 whitespace-nowrap">
                    {accommodation.roomSize} sq. m.
                  </span>
                </div>

                {/* Capacity */}
                <div className="hidden md:flex flex-row items-center gap-[5px] min-w-[120px]">
                  <SvgIcon IconComponent={PersonIcon} className="w-[18px] h-[18px] fill-brown-prime" />
                  <span className="font-pt-sans text-[15px] leading-[19px] text-gray-100">
                    x{accommodation.standardOccupancy} (max. {accommodation.maximumPeople})
                  </span>
                </div>
              </div>
            }
          >
            {/* Expanded content */}
            <div className="flex flex-col gap-[30px]">
              {/* Room Gallery */}
              {(hasGallery || accommodation.vimeo) && (
                <div className="flex flex-col gap-[10px]">
                  <MediaGallery
                    uploads={(() => {
                      const galleryUploads = transformGalleryItems(accommodation.gallery);
                      if (accommodation.vimeo) {
                        const vimeoUpload = createVimeoThumbnailUpload(accommodation.vimeo);
                        if (vimeoUpload) {
                          return [...galleryUploads, vimeoUpload];
                        }
                      }
                      return galleryUploads;
                    })()}
                    baseUrlPath=""
                    titleForModal={`${accommodation.name} - Gallery`}
                    vimeoVideoId={accommodation.vimeo?.videoId}
                    hideVimeoLink
                    enableBulkDownload
                  />
                </div>
              )}

              {/* Room description */}
              {accommodation.description && (
                <div className="flex flex-col gap-[5px]">
                  <SeeMoreTextCollapsible
                    maxLines={5}
                    className="font-pt-sans text-[15px] leading-[22px] text-black"
                    ctaClassName="!text-15px"
                  >
                    {accommodation.description}
                  </SeeMoreTextCollapsible>
                </div>
              )}

              {/* Amenities */}
              {hasAmenities && (
                <div className="flex flex-col gap-[10px]">
                  <span className="block font-pt-sans font-bold text-[17px] leading-[22px] text-black">Amenities</span>
                  <ColumnBulletPoints items={accommodation.amenities} cols={isMdOrLarger ? 3 : 2} />
                </div>
              )}

              {/* Room Benefits */}
              {hasBenefits && (
                <div className="flex flex-col gap-[10px]">
                  <span className="block font-pt-sans font-bold text-[17px] leading-[22px] text-black">
                    Room Benefits
                  </span>
                  <HtmlStringRenderer
                    content={accommodation.benefits!}
                    className="font-pt-sans text-[15px] leading-[22px] text-black"
                  />
                </div>
              )}

              {/* Room Documents */}
              {hasDocuments && (
                <div className="flex flex-col gap-[10px]">
                  <span className="block font-pt-sans font-bold text-[17px] leading-[22px] text-black">
                    Room Documents
                  </span>
                  <DocumentLinks documents={transformDocumentItems(accommodation.documents)} baseUrlPath="" />
                </div>
              )}
            </div>
          </AccordionStackable>
        );
      })}
    </div>
  );
};
